import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { landingFor } from '../lib/landing'

export function ProtectedRoute({ allowRoles }) {
  const location = useLocation()
  const { user, status } = useAuthStore()

  if (status === 'unknown') {
    return (
      <div className="min-h-screen flex items-center justify-center text-[color:var(--color-muted)] text-sm">
        Loading your workspace…
      </div>
    )
  }
  if (status === 'unauthenticated' || !user) {
    return <Navigate to="/sign-in" state={{ from: location.pathname }} replace />
  }
  if (allowRoles && !allowRoles.includes(user.role)) {
    // Signed in but wrong role — send to their natural landing page.
    return <Navigate to={landingFor(user.role)} replace />
  }
  return <Outlet />
}

/**
 * Route-level plan-feature gate. Wrap a route group whose pages require a
 * plan capability (e.g. `workflows`, `gamification`, `sops`). When the
 * tenant's plan EXCLUDES the feature, renders a "not in your plan" screen
 * instead of the page — the deep-link counterpart to hiding the nav item.
 *
 * Fail-open, mirroring backend `requireFeature` + the store's `hasFeature`:
 * enabled unless `tenant.features[feature] === false`. Super Admins (no
 * tenant) and the auth-still-loading window (tenant null → undefined) both
 * pass. The backend hard-blocks the API regardless, so this is purely UX.
 */
export function FeatureProtectedRoute({ feature, title }) {
  const user = useAuthStore((s) => s.user)
  const tenantFeatures = useAuthStore((s) => s.tenant?.features)
  const enabled =
    user?.role === 'super_admin' || tenantFeatures?.[feature] !== false
  if (enabled) return <Outlet />
  return <FeatureLockedScreen title={title} canUpgrade={user?.role === 'company_admin'} />
}

function FeatureLockedScreen({ title, canUpgrade }) {
  return (
    <div className="flex flex-1 items-center justify-center p-10">
      <div className="max-w-md text-center">
        <span className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
          <Lock size={26} />
        </span>
        <h1 className="text-xl font-semibold text-[color:var(--color-ink-strong)]">
          {title ? `${title} isn’t in your plan` : 'Not included in your plan'}
        </h1>
        <p className="mt-2 text-sm text-[color:var(--color-ink-muted)] leading-relaxed">
          This feature isn’t included in your company’s current plan.
          {canUpgrade
            ? ' Upgrade from the Billing page to unlock it.'
            : ' Contact your company administrator to upgrade.'}
        </p>
        {canUpgrade ? (
          <Link
            to="/billing"
            className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--color-accent)] hover:opacity-90"
          >
            View plans
          </Link>
        ) : null}
      </div>
    </div>
  )
}
